import { useCallback, useMemo, useState } from 'react'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Badge, Box, Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchIcon from '@mui/icons-material/Search'
import { NotificationTabs } from './notification-tabs'
import { NotificationList } from './notification-list'
import { NotificationFilterPopover } from './notification-filter-popover'
import { useNotificationFeed } from './use-notification-feed.hook'
import { useDebouncedValue } from './use-debounced-value.hook'
import { buildNotificationFilterParams } from './notification-filter-params.util'
import {
  EMPTY_NOTIFICATION_FILTERS,
  NOTIFICATION_SEARCH_DEBOUNCE_MS,
  NOTIFICATION_TABS,
} from './notification-center.const'

function NotificationCenterHeader({ onClose }) {
  const { _ } = useLingui()
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, pt: 1.5 }}>
      <Typography component="h2" sx={{ fontSize: 22, fontWeight: 700 }}><Trans>Notification Center</Trans></Typography>
      <IconButton onClick={onClose} aria-label={_(msg`Close`)}><CloseIcon /></IconButton>
    </Box>
  )
}

/**
 * Notification Center: tabs, search, filters and the grouped notification list.
 * @param {{ token: string|null, users: object[], refreshKey: number, getViewAvailability: (n: object) => string, onClose: () => void, onMarkRead: (id: number) => Promise<void>, onSelect: (n: object) => void, onView: (n: object) => void }} props
 */
export function NotificationCenter({ token, users, refreshKey, getViewAvailability, onClose, onMarkRead, onSelect, onView }) {
  const { _ } = useLingui()
  const [tab, setTab] = useState(NOTIFICATION_TABS.ALL)
  const [searchInput, setSearchInput] = useState('')
  const [filters, setFilters] = useState(EMPTY_NOTIFICATION_FILTERS)
  const [filterAnchor, setFilterAnchor] = useState(null)
  const search = useDebouncedValue(searchInput.trim(), NOTIFICATION_SEARCH_DEBOUNCE_MS)
  const filterParams = useMemo(() => buildNotificationFilterParams(filters), [filters])
  const feed = useNotificationFeed({ token, tab, search, filterParams, refreshKey })
  const { markReadLocally } = feed

  const markReadIfUnread = useCallback(async notification => {
    if (notification.status) return
    await onMarkRead(notification.id)
    markReadLocally(notification.id)
  }, [onMarkRead, markReadLocally])

  const handleSelect = useCallback(async notification => {
    await markReadIfUnread(notification)
    onSelect(notification)
  }, [markReadIfUnread, onSelect])

  const handleView = useCallback(async notification => {
    await markReadIfUnread(notification)
    onView(notification)
  }, [markReadIfUnread, onView])

  const handleApplyFilters = useCallback(nextFilters => {
    setFilters(nextFilters)
    setFilterAnchor(null)
  }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <NotificationCenterHeader onClose={onClose} />
      <Box sx={{ px: 1.5, py: 1 }}><NotificationTabs activeTab={tab} summary={feed.summary} onChange={setTab} /></Box>
      <Box sx={{ display: 'flex', gap: 1, px: 1.5, pb: 1 }}>
        <TextField
          size="small" fullWidth value={searchInput} onChange={event => setSearchInput(event.target.value)}
          placeholder={_(msg`Search notifications (crash ID, user, keyword...)`)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Badge color="primary" variant="dot" invisible={filterParams.length === 0}>
          <Button variant="outlined" startIcon={<FilterListIcon />} endIcon={<ExpandMoreIcon />} onClick={event => setFilterAnchor(event.currentTarget)}><Trans>Filter</Trans></Button>
        </Badge>
      </Box>
      <NotificationFilterPopover anchorEl={filterAnchor} filters={filters} users={users} onApply={handleApplyFilters} onClose={() => setFilterAnchor(null)} />
      <NotificationList
        items={feed.items}
        isLoading={feed.isLoading}
        hasError={feed.hasError}
        hasMore={feed.hasMore}
        getViewAvailability={getViewAvailability}
        onLoadMore={feed.loadMore}
        onSelect={handleSelect}
        onView={handleView}
      />
    </Box>
  )
}
